import { Box, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import "./App.scss";
import { AuthService } from "./app/auth/service";
import LoadingSpinner from "./shared/components/LoadingSpinner";
import { ETheme } from "./shared/enums/user/ETheme";
import { handleError } from "./shared/functions/handleError";
import { ILayout } from "./shared/interfaces/shared/ILayout";
import Layout from "./shared/layout/Layout";
import {
  darkTheme,
  lightTheme,
  StyledMaterialDesignContent,
} from "./shared/mui/theme";
import authSlice from "./store/slices/auth.slice";
import enumsSlice from "./store/slices/enums.slice";
import { RootState } from "./store/store";
import i18n from "./utils/localization/i18n";
import { routeList } from "./utils/routes";
import { publicRoutes } from "./utils/routes/roles/publicRoutes";

function App() {
  const dispatch = useDispatch();
  const jwtToken = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const { userData, isAuthenticated } = useSelector(
    (state: RootState) => state.auth,
  );
  const company = useSelector((state: RootState) => state?.companySettings);
  const currentRoleRoutes = routeList?.find(
    (route) => route?.role === userData?.role,
  );

  useEffect(() => {
    Promise.all([AuthService.enums()])
      .then(async ([enumsResponse]) => {
        dispatch(enumsSlice.actions.setEnums(enumsResponse));
        localStorage.setItem("primaryColor", "#431e5b");
        localStorage.setItem("currencyCode", "EUR");
      })
      .catch(handleError);
  }, [dispatch, jwtToken]);

  useEffect(() => {
    if (jwtToken) {
      setIsLoading(true);
      Promise.all([AuthService.me(jwtToken as string)])
        .then(async ([userResponse]) => {
          localStorage.setItem("language", userResponse?.language ?? "gb");
          await i18n.changeLanguage(userResponse?.language);
          dispatch(
            authSlice.actions.setLoginState({
              userData: userResponse,
              token: jwtToken,
              isAuthenticated: true,
            }),
          );
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [dispatch, jwtToken]);

  useEffect(() => {
    if (userData?.theme) {
      setIsDarkTheme(userData?.theme === ETheme.DARK);
      if (userData?.theme === ETheme.LIGHT) {
        document.body.classList.remove("dark");
      } else {
        document.body.classList.add(userData?.theme);
      }
    }
  }, [userData?.theme]);

  useEffect(() => {
    if (company?.primaryColor) {
      document.documentElement.style.setProperty(
        "--primaryColor",
        company?.primaryColor,
      );
    }
  }, [company?.primaryColor]);

  if (isLoading) {
    // Display loading spinner while checking authentication
    return <LoadingSpinner isDarkTheme={isDarkTheme} />;
  }
  return (
    <ThemeProvider
      theme={
        isDarkTheme
          ? {
              ...darkTheme,
              palette: {
                ...darkTheme.palette,
                primary: {
                  ...darkTheme.palette.primary,
                  main: company?.primaryColor,
                  dark: `${company?.primaryColor}0D`,
                },
              },
            }
          : {
              ...lightTheme,
              palette: {
                ...lightTheme.palette,
                primary: {
                  ...lightTheme.palette.primary,
                  main: company?.primaryColor,
                  dark: `${company?.primaryColor}0D`,
                },
              },
            }
      }
    >
      <SnackbarProvider
        maxSnack={3}
        Components={{
          success: StyledMaterialDesignContent,
          error: StyledMaterialDesignContent,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <Box
          sx={{
            minHeight: "100vh",
            backgroundColor: "background.default",
            color: "text.primary",
          }}
        >
          {isAuthenticated ? (
            <Layout
              routes={(currentRoleRoutes?.routes ?? []) as unknown as ILayout[]}
            >
              <Routes>
                {currentRoleRoutes?.routes?.map((route) => {
                  return (
                    <Route
                      path={route?.path}
                      element={route?.component}
                      key={route?.path}
                    />
                  );
                })}
              </Routes>
            </Layout>
          ) : (
            <Routes>
              {publicRoutes?.map((route) => {
                return (
                  <Route
                    path={route?.path}
                    element={route?.component}
                    key={route?.path}
                  />
                );
              })}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          )}
        </Box>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
